import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Subject } from 'rxjs/Subject';

/**
 * Native Web Push API implementation
 * Uses modern browser push notifications with VAPID
 * @implements {IChilduppNotification}
 */
export class NativeWebPushNotification {
    constructor() {
        // Setup object properties and observables
        Object.defineProperties(this, {
            /**
             * @private
             * @type {BehaviorSubject<boolean|undefined>}
             */
            _hasPermission$: {
                value: new BehaviorSubject(undefined),
            },
            /**
             * @private
             * @type {BehaviorSubject<string|null|undefined>}
             */
            _token$: {
                value: new BehaviorSubject(undefined),
            },
            /**
             * @private
             * @type {Subject<Notification>}
             */
            _notifications$: {
                value: new Subject(),
            },
        });

        // Initialize permission state
        this._updatePermissionState();
        
        // Listen for service worker messages
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', (event) => {
                if (event.data && event.data.type === 'PUSH_NOTIFICATION') {
                    this._notifications$.next({
                        title: event.data.title,
                        body: event.data.body,
                        icon: event.data.icon,
                    });
                }
            });
        }
    }

    /**
     * @inheritDoc
     */
    name() {
        return "NativeWebPush@1.0.0-" + (typeof __webpack_require__ !== 'undefined' ? 'cdb4098cf17ef5342a56' : 'dev');
    }

    /**
     * @inheritDoc
     */
    requestPermission() {
        console.log('[NativeWebPush] v1.0.0-cdb4098cf17ef5342a56 - requestPermission called');
        
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            console.warn('[NativeWebPush] Push messaging is not supported');
            this._hasPermission$.next(false);
            return Promise.resolve(false);
        }

        console.log('[NativeWebPush] Current permission state:', Notification.permission);
        
        return Notification.requestPermission()
            .then((permission) => {
                console.log('[NativeWebPush] Permission response:', permission);
                const hasPermission = permission === 'granted';
                this._hasPermission$.next(hasPermission);
                
                if (hasPermission) {
                    return this._getOrCreateSubscription();
                }
                
                return false;
            })
            .catch((error) => {
                console.error('[NativeWebPush] Error requesting permission:', error);
                this._hasPermission$.next(false);
                return false;
            });
    }

    /**
     * @inheritDoc
     */
    hasPermission() {
        return this._hasPermission$;
    }

    /**
     * @inheritDoc
     */
    getToken() {
        return this._token$;
    }

    /**
     * @inheritDoc
     */
    onNotificationReceived() {
        return this._notifications$;
    }

    /**
     * @inheritDoc
     */
    onNotification(cb) {
        const subscription = this._notifications$.subscribe(cb);
        return function() {
            subscription.unsubscribe();
        };
    }

    /**
     * @inheritDoc
     */
    onTokenRefresh(cb) {
        const subscription = this._token$
            .filter(function(token) { return token !== undefined; })
            .subscribe(cb);
        return function() {
            subscription.unsubscribe();
        };
    }

    /**
     * Update permission state based on current browser permission
     * @private
     */
    _updatePermissionState() {
        if (!('Notification' in window)) {
            this._hasPermission$.next(false);
            return;
        }

        const permission = Notification.permission;
        if (permission === 'granted') {
            this._hasPermission$.next(true);
            this._getOrCreateSubscription();
        } else if (permission === 'denied') {
            this._hasPermission$.next(false);
        } else {
            this._hasPermission$.next(undefined);
        }
    }

    /**
     * Get or create push subscription
     * @private
     */
    _getOrCreateSubscription() {
        console.log('[NativeWebPush] v1.0.0-cdb4098cf17ef5342a56 - _getOrCreateSubscription called');
        console.log('[NativeWebPush] UPPLICATION_CONFIG:', window.UPPLICATION_CONFIG);
        
        if (!window.UPPLICATION_CONFIG || !window.UPPLICATION_CONFIG.VAPID_PUBLIC_KEY) {
            console.error('[NativeWebPush] No VAPID public key configured');
            return Promise.resolve(null);
        }

        return navigator.serviceWorker.ready
            .then((registration) => {
                return registration.pushManager.getSubscription()
                    .then((subscription) => {
                        if (subscription) {
                            return subscription;
                        }

                        // Create new subscription
                        const vapidPublicKey = window.UPPLICATION_CONFIG.VAPID_PUBLIC_KEY;
                        const applicationServerKey = this._urlBase64ToUint8Array(vapidPublicKey);

                        return registration.pushManager.subscribe({
                            userVisibleOnly: true,
                            applicationServerKey: applicationServerKey
                        });
                    });
            })
            .then((subscription) => {
                if (subscription) {
                    const token = JSON.stringify(subscription);
                    this._token$.next(token);
                    return token;
                }
                return null;
            })
            .catch((error) => {
                console.error('[NativeWebPush] Error getting subscription:', error);
                this._token$.next(null);
                return null;
            });
    }

    /**
     * Convert VAPID public key to Uint8Array
     * @private
     */
    _urlBase64ToUint8Array(base64String) {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/\-/g, '+')
            .replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }
}


// WEBPACK FOOTER //
// ./src/shared/childupp-notifications/native-webpush-notification.js